import axios from "axios";
import { useMemo } from "react";
import { useAuth } from "@/lib/auth";

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;
export const fileUrl = (p) => `${API}/files/${p}`;

export function useApi() {
  const { getToken, mode, signOut } = useAuth();
  return useMemo(() => {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use(async (c) => {
      const t = await getToken();
      if (t) c.headers.Authorization = `Bearer ${t}`;
      return c;
    });
    inst.interceptors.response.use((r) => r, (e) => {
      if (mode === "local" && e?.response?.status === 401) signOut();
      return Promise.reject(e);
    });
    inst.getToken = getToken;
    return inst;
  }, [getToken, mode, signOut]);
}

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return d || "Algo deu errado. Tente novamente.";
};

export const TERMS = {
  BR: { state: "Estado", city: "Cidade", district: "Bairro", postal: "CEP", address: "Endereço", phone: "Celular (com DDD)",
        doc: "CPF", prefix: "+55", flag: "🇧🇷", name: "Brasil", docPh: "000.000.000-00", postalPh: "00000-000", phonePh: "11 91234-5678" },
  PT: { state: "Distrito", city: "Concelho", district: "Freguesia", postal: "Código postal", address: "Morada", phone: "Telemóvel",
        doc: "NIF", prefix: "+351", flag: "🇵🇹", name: "Portugal", docPh: "123456789", postalPh: "1000-001", phonePh: "912 345 678" },
};

const PT_SPECS = { Encanador: "Canalizador", Pedreiro: "Trolha / Pedreiro", "Marido de aluguel": "Faz-tudo", Gesseiro: "Estucador", Azulejista: "Ladrilhador" };
export const specLabel = (s, c) => (c === "PT" ? PT_SPECS[s] || s : s);

export const money = (v, c) =>
  new Intl.NumberFormat(c === "PT" ? "pt-PT" : "pt-BR", { style: "currency", currency: c === "PT" ? "EUR" : "BRL" }).format(v || 0);

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export const STATUS = {
  open: { label: "Aberto", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  assigned: { label: "Em andamento", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  completed: { label: "Concluído", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  cancelled: { label: "Cancelado", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  pending: { label: "Pendente", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  accepted: { label: "Aceita", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  rejected: { label: "Recusada", cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
};

export const DEFAULT_STORY = `Tudo começou com uma infiltração. Numa noite de chuva, a água começou a escorrer pela parede do quarto. No dia seguinte veio a maratona: ligações sem resposta, um pedreiro que prometeu vir e não veio, três orçamentos tão diferentes que ninguém sabia qual era justo. Foram semanas de espera, dinheiro perdido e a sensação de estar sozinho diante de um problema simples.

Do outro lado, conhecemos o Seu Antônio, pedreiro há 25 anos, excelente no que faz, mas que passava dias parado porque o trabalho só chegava pelo boca a boca.

Percebemos que os dois lados tinham o mesmo problema: falta de confiança e de tempo. O Zelobra nasceu para resolver isso — uma foto vira diagnóstico, o preço justo deixa de ser mistério, e o bom profissional é encontrado por quem precisa dele, perto de casa, no Brasil e em Portugal.

Zelobra. Sua obra, sem dor de cabeça.`;

export function toCSV(rows) {
  if (!rows.length) return "";
  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))];
  const esc = (v) => {
    const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  return "\uFEFF" + [cols.join(";"), ...rows.map((r) => cols.map((c) => esc(r[c])).join(";"))].join("\n");
}
